import React, { useState, useEffect } from 'react';
import { PageHeader } from '../design';
import { useParams, useNavigate } from 'react-router-dom';
import { useSuperAdminAuth } from '../../../contexts/SuperAdminAuthContext';
import { governanceAPI } from '../../../lib/governanceApi';
import { toast } from 'sonner';
import {
  Building2,
  ArrowLeft,
  RefreshCw,
  Users,
  Shield,
  Activity,
  DollarSign,
  TrendingUp,
  Mail,
  Calendar,
  FileCheck,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Power,
  PowerOff,
  Zap,
  Bot,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Skeleton } from '../../../components/ui/skeleton';
import { Progress } from '../../../components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';

export default function OrganizationDetail() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useSuperAdminAuth();
  const [loading, setLoading] = useState(true);
  const [orgData, setOrgData] = useState(null);
  const [orgSummary, setOrgSummary] = useState(null);

  useEffect(() => {
    if (isAuthenticated && orgId) {
      fetchOrgDetail();
    }
  }, [isAuthenticated, orgId]);

  const fetchOrgDetail = async () => {
    setLoading(true);
    try {
      // Fetch both enhanced list (for summary metrics) and detail
      const [detailRes, enhancedRes] = await Promise.all([
        governanceAPI.getOrganizationDetail(orgId),
        governanceAPI.getOrganizationsEnhanced()
      ]);
      
      setOrgData(detailRes.data);
      
      // Find the org summary from enhanced list
      const summary = enhancedRes.data?.find(o => o.org_id === orgId);
      setOrgSummary(summary);
    } catch (error) {
      console.error('Failed to fetch org detail:', error);
      toast.error('Failed to load organization details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return `$${(amount || 0).toFixed(2)}`;
  };

  const getMaxExecutions = () => {
    if (!orgData?.activity_trend) return 1;
    return Math.max(...orgData.activity_trend.map(d => d.executions), 1);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <Skeleton className="h-8 w-64" />
        </div>
        <div className="grid gap-4 md:grid-cols-4">
          <Skeleton className="h-28" />
          <Skeleton className="h-28" />
          <Skeleton className="h-28" />
          <Skeleton className="h-28" />
        </div>
        <Skeleton className="h-64" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (!orgData) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Building2 className="h-12 w-12 text-muted-foreground mb-4" />
        <h2 className="text-xl font-semibold mb-2">Organization Not Found</h2>
        <p className="text-muted-foreground mb-4">The requested organization could not be loaded.</p>
        <Button onClick={() => navigate('/governance/organizations')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Organizations
        </Button>
      </div>
    );
  }

  const org = orgData.organization;

  return (
    <div className="space-y-6" data-testid="organization-detail">
      {/* Header */}
      <PageHeader
        breadcrumb={
          <button onClick={() => navigate('/governance/organizations')} className="inline-flex items-center gap-1.5 hover:gov-text-primary transition-colors">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Organizations
          </button>
        }
        title={org.name || orgSummary?.name || <Skeleton className="inline-block h-7 w-48 align-middle" />}
        subtitle={
          <span className="inline-flex items-center gap-2">
            <span className="font-mono">{orgId}</span>
            {orgSummary?.owner_email && (
              <>
                <span>•</span>
                <Mail className="h-3 w-3" />
                <span>{orgSummary.owner_email}</span>
              </>
            )}
          </span>
        }
        actions={
          <>
            {orgSummary?.kill_switch_active ? (
              <Badge variant="destructive" className="text-sm px-3 py-1">
                <PowerOff className="h-4 w-4 mr-1" />
                AI PAUSED
              </Badge>
            ) : (
              <Badge variant="default" className="bg-green-600 text-sm px-3 py-1">
                <Power className="h-4 w-4 mr-1" />
                AI ENABLED
              </Badge>
            )}
            <Button variant="outline" size="icon" onClick={fetchOrgDetail}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </>
        }
      />

      {/* Summary Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4 lg:grid-cols-8">
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-500" />
              <div>
                <div className="text-xl font-bold">{orgSummary?.total_users || orgData.user_summary?.total || 0}</div>
                <p className="text-xs text-muted-foreground">Total Users</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-purple-500" />
              <div>
                <div className="text-xl font-bold">{orgData.tenants?.length || 0}</div>
                <p className="text-xs text-muted-foreground">Tenants</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-amber-500" />
              <div>
                <div className="text-xl font-bold">{orgSummary?.ai_executions_24h || 0}</div>
                <p className="text-xs text-muted-foreground">Exec (24h)</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-indigo-500" />
              <div>
                <div className="text-xl font-bold">{orgSummary?.ai_executions_30d || 0}</div>
                <p className="text-xs text-muted-foreground">Exec (30d)</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-green-500" />
              <div>
                <div className="text-xl font-bold">{formatCurrency(orgSummary?.ai_cost_mtd)}</div>
                <p className="text-xs text-muted-foreground">Cost (MTD)</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <FileCheck className="h-5 w-5 text-teal-500" />
              <div>
                <div className="text-xl font-bold">{orgSummary?.approval_rate || 0}%</div>
                <p className="text-xs text-muted-foreground">Approval Rate</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-orange-500" />
              <div>
                <div className="text-xl font-bold">{orgSummary?.pending_approvals || 0}</div>
                <p className="text-xs text-muted-foreground">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              <div>
                <div className="text-xl font-bold">{orgSummary?.budget_risk_count || 0}</div>
                <p className="text-xs text-muted-foreground">Budget Risks</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Tenants Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Tenants ({orgData.tenants?.length || 0})
            </CardTitle>
            <CardDescription>All workspaces under this organization</CardDescription>
          </CardHeader>
          <CardContent>
            {orgData.tenants?.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No tenants configured
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tenant</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Executions (30d)</TableHead>
                    <TableHead className="text-right">Cost (30d)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orgData.tenants?.map((tenant) => (
                    <TableRow key={tenant.tenant_id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{tenant.name}</div>
                          <div className="text-xs text-muted-foreground font-mono">
                            {tenant.tenant_id?.substring(0, 8)}...
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {tenant.kill_switch_active ? (
                          <Badge variant="destructive">Paused</Badge>
                        ) : (
                          <Badge variant="default" className="bg-green-600">Active</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {tenant.ai_executions_30d || 0}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(tenant.ai_cost_30d)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* User Summary Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              User Summary
            </CardTitle>
            <CardDescription>User distribution across roles and status</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg text-center">
                <div className="text-3xl font-bold text-primary">{orgData.user_summary?.total || 0}</div>
                <div className="text-sm text-muted-foreground mt-1">Total Users</div>
              </div>
              <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg text-center">
                <div className="text-3xl font-bold text-purple-600">{orgData.user_summary?.admins || 0}</div>
                <div className="text-sm text-muted-foreground mt-1">Admins</div>
              </div>
              <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg text-center">
                <div className="text-3xl font-bold text-green-600">{orgData.user_summary?.active || 0}</div>
                <div className="text-sm text-muted-foreground mt-1">Active</div>
              </div>
              <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg text-center">
                <div className="text-3xl font-bold text-slate-400">{orgData.user_summary?.inactive || 0}</div>
                <div className="text-sm text-muted-foreground mt-1">Inactive</div>
              </div>
            </div>
            
            {/* User Activity Bar */}
            <div className="mt-6">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-muted-foreground">Active vs Inactive</span>
                <span className="font-medium">
                  {orgData.user_summary?.total > 0 
                    ? Math.round((orgData.user_summary?.active / orgData.user_summary?.total) * 100)
                    : 0}% Active
                </span>
              </div>
              <Progress 
                value={orgData.user_summary?.total > 0 
                  ? (orgData.user_summary?.active / orgData.user_summary?.total) * 100 
                  : 0} 
                className="h-2"
              />
            </div>
          </CardContent>
        </Card>

        {/* Activity Trend Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              AI Activity Trend (Last 7 Days)
            </CardTitle>
            <CardDescription>Daily AI execution count</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-48 flex items-end gap-2 px-2">
              {orgData.activity_trend?.map((day, i) => {
                const maxExec = getMaxExecutions();
                const height = maxExec > 0 ? (day.executions / maxExec) * 100 : 0;
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-2">
                    <div className="w-full flex flex-col items-center justify-end h-36">
                      <span className="text-xs font-medium mb-1">{day.executions}</span>
                      <div 
                        className="w-full bg-primary/80 rounded-t transition-all duration-300 hover:bg-primary"
                        style={{ height: `${Math.max(height, 5)}%` }}
                      />
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' })}
                    </span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Organization Info Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Organization Details
            </CardTitle>
            <CardDescription>Configuration, subscription and metadata</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Organization ID - Enhanced Display */}
              <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <div className="text-xs text-muted-foreground mb-1">Organization ID</div>
                <div className="font-mono text-sm break-all select-all">{orgId}</div>
              </div>
              
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-muted-foreground">Registration Date</span>
                <span className="font-medium">{formatDate(org.created_at)}</span>
              </div>
              
              {/* Subscription Section */}
              <div className="pt-2">
                <div className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  Subscription & Billing
                </div>
                <div className="space-y-3 pl-2">
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-muted-foreground">Plan</span>
                    <Badge 
                      variant={orgData.subscription?.plan_id === 'enterprise' ? 'default' : 
                               orgData.subscription?.plan_id === 'pro' ? 'default' : 'outline'}
                      className={`capitalize ${
                        orgData.subscription?.plan_id === 'enterprise' ? 'bg-purple-600' :
                        orgData.subscription?.plan_id === 'pro' ? 'bg-blue-600' :
                        orgData.subscription?.plan_id === 'starter' ? 'bg-green-600' : ''
                      }`}
                    >
                      {orgData.subscription?.plan_id || orgSummary?.plan_type || 'Free'}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-muted-foreground">Billing Status</span>
                    <Badge 
                      variant={orgData.subscription?.billing_status === 'active' ? 'default' : 'destructive'}
                      className={orgData.subscription?.billing_status === 'active' ? 'bg-green-600' : ''}
                    >
                      {orgData.subscription?.billing_status || orgSummary?.billing_status || 'Active'}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-muted-foreground">Subscription Since</span>
                    <span>{orgData.subscription?.subscription_start ? formatDate(orgData.subscription.subscription_start) : 'N/A'}</span>
                  </div>
                  {orgData.subscription?.stripe_customer_id && (
                    <div className="flex justify-between items-center py-2 border-b">
                      <span className="text-muted-foreground">Stripe Customer</span>
                      <span className="font-mono text-xs">{orgData.subscription.stripe_customer_id}</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Last Login Section */}
              <div className="pt-2">
                <div className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Last Login
                </div>
                <div className="space-y-3 pl-2">
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-muted-foreground">User</span>
                    <span className="flex items-center gap-2">
                      <Mail className="h-3 w-3 text-muted-foreground" />
                      {orgData.last_login?.user_email || 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-muted-foreground">Date & Time</span>
                    <span>{orgData.last_login?.last_login ? formatDate(orgData.last_login.last_login) : 'Never'}</span>
                  </div>
                </div>
              </div>
              
              {/* AI Usage Section */}
              <div className="pt-2">
                <div className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                  <Activity className="h-4 w-4" />
                  AI Usage
                </div>
                <div className="space-y-3 pl-2">
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-muted-foreground">Most Used Agent</span>
                    <span className="capitalize">{orgSummary?.most_used_agent || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-muted-foreground">Last AI Activity</span>
                    <span>{orgSummary?.last_activity ? formatDate(orgSummary.last_activity) : 'N/A'}</span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-muted-foreground">Kill Switch Events (30d)</span>
                    <span className={orgSummary?.kill_switch_events_30d > 0 ? 'text-amber-600 font-medium' : ''}>
                      {orgSummary?.kill_switch_events_30d || 0}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Audit Logs */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileCheck className="h-5 w-5" />
            Recent Audit Logs
          </CardTitle>
          <CardDescription>Last 10 governance actions for this organization</CardDescription>
        </CardHeader>
        <CardContent>
          {orgData.recent_audit?.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No audit logs found
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Action</TableHead>
                  <TableHead>Initiated By</TableHead>
                  <TableHead>Scope</TableHead>
                  <TableHead>Details</TableHead>
                  <TableHead className="text-right">Timestamp</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orgData.recent_audit?.map((log, i) => (
                  <TableRow key={log.id || log.audit_id || i}>
                    <TableCell>
                      <Badge variant={
                        log.action?.includes('FAILED') ? 'destructive' :
                        log.action?.includes('ON') || log.action?.includes('ENABLE') ? 'default' :
                        'secondary'
                      }>
                        {log.action?.replace(/_/g, ' ') || 'Unknown'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{log.initiated_by || log.user_id?.substring(0, 8) || 'System'}</span>
                    </TableCell>
                    <TableCell>
                      <span className="capitalize text-sm">{log.scope || 'org'}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-muted-foreground max-w-[200px] truncate block">
                        {log.reason || log.details?.error?.substring(0, 50) || '-'}
                      </span>
                    </TableCell>
                    <TableCell className="text-right text-sm text-muted-foreground">
                      {formatDate(log.timestamp)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
